// WhiteLabelConfig.js
// Configuration for white labeling the assessment page based on customer ID

/**
 * White label configuration object
 * 
 * Properties for each customer:
 * - primaryColor: Main color for buttons and header elements (format: hex color string)
 * - secondaryColor: Secondary color for accent elements (format: hex color string)
 * - popupTitle: Title to display in the modal popup (format: string)
 * - popupText: Text content to display in the modal popup (format: string)
 * - customLogoPath: Path to custom logo (format: string, optional)
 * - supportEmail: Custom support email address (format: string, optional)
 * - showNavigation: Whether to display navigation elements (header, footer, side navigation) (format: boolean, default: false)
 * - loadingMessage: Custom loading message for report generation (format: string)
 * - paymentProcessingMessage: Custom loading message for payment processing (format: string)
 * - successTitle: Custom success message title prefix (format: string)
 * - successSubtitle: Custom success message subtitle (format: string)
 * - emailNotification: Custom email notification message (format: string)
 */

// Email placeholder for the popup text, will be replaced with the actual support email
// const SUPPORT_EMAIL_PLACEHOLDER = '{{SUPPORT_EMAIL}}';

export const WHITE_LABEL_CONFIG = {
  // Default configuration used when no matching customer ID is found
  default: {
    primaryColor: "#00c067", // Default green color
    secondaryColor: "#646368", // Default gray for secondary buttons
    popupTitle: "Assessment Completed",
    popupText: "You have already created one free assessment report from your subscription plan. To purchase additional assessment reports, please use the 'Buy Report' button.",
    supportEmail: "contactus@speakho.co",
    showNavigation: true, // show navigation elements for default config
    loadingMessage: "Please wait - Generating Assessment Report",
    paymentProcessingMessage: "Please wait - Processing Payment",
    successTitle: "Thank You for Your Assessment",
    successSubtitle: "You can view the report with the Download Report button.",
    emailNotification: "We will send your assessment report to your registered email ID.",
  },
  
  // Configuration for Pronunciation Pro (Customer ID: 1)
  1: {
    primaryColor: "#DD6D2E", // Orange color (Primary) for Pronunciation Pro
    secondaryColor: "#2E4F62", // Dark blue color (Secondary) for Pronunciation Pro
    accentColor: "#DD6D2E", // Orange accent color from Pronunciation Pro color scheme
    tertiaryColor1: "#96AAB5", // Light blue-gray from Pronunciation Pro color scheme
    tertiaryColor2: "#4C6D7E", // Medium blue from Pronunciation Pro color scheme
    popupTitle: "Assessment Completed",
    popupText: "It looks like you have already completed your assessment report. Your next step is to start working through your training program. If you have any questions, please contact us at support@pronunciationpro.com.",
    supportEmail: "support@pronunciationpro.com",
    showNavigation: false, // Hide navigation elements for Pronunciation Pro
    loadingMessage: "Please wait - Generating your Pronunciation Pro Assessment Report",
    paymentProcessingMessage: "Please wait - Processing your Pronunciation Pro Payment",
    successTitle: "Thank You for Your Pronunciation Assessment",
    successSubtitle: "",
    emailNotification: "We will send your pronunciation assessment report to your registered email ID.",
    },
  
  // Configuration for Speakho (Customer ID: 2)
  2: {
    primaryColor: "#00c067", 
    secondaryColor: "#646368",
    popupTitle: "Assessment Completed",
    popupText: "You've completed your assessment. To access additional reports, please purchase using the button below. If you need assistance, contact us at contactus@speakho.co.",
    supportEmail: "contactus@speakho.co",
    showNavigation: true, // Show navigation elements for Speakho
    loadingMessage: "Please wait - Generating Assessment Report",
    paymentProcessingMessage: "Please wait - Processing Payment",
    successTitle: "Thank You for Your Assessment",
    successSubtitle: "You can view the report with the Download Report button.",
    emailNotification: "We will send your assessment report to your registered email ID.",
  },
  
  // Example configuration for another customer (ID: 3)
  3: {
    primaryColor: "#722ed1", // Purple color
    secondaryColor: "#595959",
    popupTitle: "Assessment Already Completed",
    popupText: "You have already completed an assessment. For any assistance, please contact our support team at support@customer3.com.",
    supportEmail: "support@customer3.com",
    showNavigation: false, // Hide navigation elements for this customer
    loadingMessage: "Please wait - Generating Assessment Report",
    paymentProcessingMessage: "Please wait - Processing Payment",
    successTitle: "Thank You for Your Assessment",
    successSubtitle: "You can view the report with the Download Report button.",
    emailNotification: "We will send your assessment report to your registered email ID.",
  }
};

/**
 * Get white label configuration for a customer
 * @param {number|string} customerId - Customer ID
 * @returns {object} - White label configuration object
 */
export const getWhiteLabelConfig = (customerId) => {
  // If customerId exists and has a configuration, return it
  if (customerId && WHITE_LABEL_CONFIG[customerId]) {
    return WHITE_LABEL_CONFIG[customerId];
  }
  
  // Otherwise return default configuration
  return WHITE_LABEL_CONFIG.default;
};
